import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { PhoneNumberUtil } from "google-libphonenumber";

import { ActionButton, FormDialog, InputField, ToggleButton, PhoneInput, SelectField } from "../v3";
import { CRMService } from "../../services";
import { clientValidationRules } from "../../pages/client/validation";
import { GST_STATE_MAP } from "../../constants/gstStateMap";

const phoneUtil = PhoneNumberUtil.getInstance();

const stateOptions = Object.entries(GST_STATE_MAP).map(([id, name]) => ({
  id,
  name,
}));

const getStateOptions = async ({ name = "" } = {}) => ({
  data: stateOptions.filter((option) => option.name.toLowerCase().includes(name.toLowerCase())),
});
const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

export const AddEditClient = ({ open, onClose, clientId, onSuccess }) => {
  const [showGst, setShowGst] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);

  const [clientDetails, setClientDetails] = useState({
    name: undefined,
    primaryPhone: undefined,
    primaryAddress: {
      locality: undefined,
      state: "",
      stateCode: "",
    },
    taxId: undefined,
  });

  const isValid = isPhoneValid(clientDetails.primaryPhone);
  const showError = touched && !isValid;
  useEffect(() => {
    if (open && clientId) {
      fetchData(clientId);
    } else if (open && !clientId) {
      setClientDetails({
        name: undefined,
        primaryPhone: undefined,
        primaryAddress: {
          locality: undefined,
          state: "",
          stateCode: "",
        },
        taxId: undefined,
      });
    }
  }, [open, clientId]);

  const fetchData = async (id) => {
    try {
      const response = await CRMService.getClientById(id);

      const data = response?.data;
      const taxId = data?.taxId || "";

      const stateCode = data?.primaryAddress?.stateCode || "";
      const state = data?.primaryAddress?.state || "";

      setClientDetails({
        name: data?.name || undefined,
        primaryPhone: data?.primaryPhone || undefined,
        primaryAddress: {
          locality: data?.primaryAddress?.locality || undefined,
          state,
          stateCode,
        },
        taxId: taxId || undefined,
      });

      setShowGst(!!taxId);
    } catch (error) {
      toast("Failed to fetch client details", { type: "error" });
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    const trimmedValue = typeof value === "string" ? value.trimStart() : value;

    if (name === "taxId") {
      setClientDetails((prev) => ({
        ...prev,
        taxId: String(trimmedValue).toUpperCase(),
      }));
      return;
    }

    if (name === "locality") {
      setClientDetails((prev) => ({
        ...prev,
        primaryAddress: {
          ...prev.primaryAddress,
          locality: trimmedValue,
        },
      }));
    } else {
      setClientDetails((prev) => ({
        ...prev,
        [name]: trimmedValue,
      }));
    }
  };
  const handleStateChange = (selectedState) => {
    setClientDetails((prev) => ({
      ...prev,
      primaryAddress: {
        ...prev.primaryAddress,
        state: selectedState?.name || "",
        stateCode: selectedState?.id || "",
      },
    }));
  };
  const handleCloseModal = () => {
    setErrors({});
    setTouched(false);
    setShowGst(false);
    onClose();
  };

  const handleToggleChange = (e) => {
    setShowGst(e.target.checked);
  };

  const handleSaveClient = async () => {
    setTouched(true);
    setErrors({});

    const validationErrors = clientValidationRules(clientDetails);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      ...clientDetails,
    };

    payload.primaryAddress = {
      locality: clientDetails.primaryAddress.locality || "",
      state: clientDetails.primaryAddress.state || "",
      stateCode: clientDetails.primaryAddress.stateCode || "",
      country: "",
      pinCode: "",
    };

    try {
      setLoading(true);

      if (clientId) {
        await CRMService.updateClient(clientId, payload);
        toast("Client updated successfully", { type: "success" });
      } else {
        await CRMService.createClient(payload);
        toast("Client added successfully", { type: "success" });
      }

      if (onSuccess) {
        onSuccess();
      }

      handleCloseModal();
    } catch (error) {
      let errorMessage = `Error ${clientId ? "updating" : "adding"} client`;

      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast(errorMessage, { type: "error" });

      console.error("🚀 ~ handleSaveClient ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title={clientId ? "Edit Client" : "Add Client"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />

          <ActionButton buttonText="Save" onButtonClick={handleSaveClient} disabled={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Client Name"
            name="name"
            value={clientDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />

          <InputField
            label="place"
            name="locality"
            value={clientDetails.primaryAddress.locality}
            onChange={updateDetailsState}
            fullWidth
          />
        </Box>

        <Box display="flex" gap={2}>
          <PhoneInput
            label="Mobile Number"
            defaultCountry="in"
            value={clientDetails.primaryPhone}
            size="small"
            fullWidth
            required
            onChange={(e164) =>
              setClientDetails((prev) => ({
                ...prev,
                primaryPhone: e164,
              }))
            }
            onBlur={() => setTouched(true)}
            error={showError || !!errors.primaryPhone}
            helperText={
              errors.primaryPhone ||
              (showError ? (!clientDetails.primaryPhone ? "Mobile number is required" : "Enter a valid phone number") : " ")
            }
          />
        </Box>

        <Box display="flex" alignItems="center" ml={1} mb={1}>
          <ToggleButton label="GST Number" checked={showGst} onToggleChange={handleToggleChange} />
        </Box>

        {showGst && (
          <>
            <Box display="flex" gap={2}>
              <InputField label="GST Number" name="taxId" value={clientDetails.taxId} onChange={updateDetailsState} fullWidth />
            </Box>

            <Box display="flex" gap={2}>
              <SelectField
                label="State"
                value={stateOptions.find((option) => option.id === clientDetails.primaryAddress.stateCode) || null}
                getData={getStateOptions}
                onChange={handleStateChange}
                style={{ width: "100%" }}
              />

              <InputField disabled label="State Code" value={clientDetails.primaryAddress.stateCode} fullWidth />
            </Box>
          </>
        )}
      </Box>
    </FormDialog>
  );
};
